import { format, isSameDay } from 'date-fns';
import { ScrollArea } from '@/components/ui/scroll-area';
import { TaskListItem } from './TaskListItem';
import { compareTasksByCompletionThenPriority, type TaskClaimSummary } from '@/lib/task-filters';
import type { ClaimTask } from '@/api/generated/client';

interface TaskListProps {
  tasks: ClaimTask[];
  claimsMap: Map<number, TaskClaimSummary>;
  selectedDate?: Date;
  title?: string;
  onTaskComplete?: (taskId: number, claimId: number) => void;
  onTaskEdit?: (task: ClaimTask) => void;
}

export function TaskList({ tasks, claimsMap, selectedDate, title, onTaskComplete, onTaskEdit }: TaskListProps) {
  const now = new Date();
  const displayDate = selectedDate || now;
  
  // Determine title based on selected date
  const displayTitle = title || (
    selectedDate 
      ? `Tasks for ${format(selectedDate, 'MMMM d, yyyy')}` 
      : `Tasks for Today`
  );

  // Filter tasks for the selected day, ordered with active tasks before completed
  // ones, then by priority within each group.
  const dayTasks = tasks.filter(task => {
    if (!task.dueDate) return false;
    const dueDate = new Date(task.dueDate);
    return isSameDay(dueDate, displayDate);
  }).sort(compareTasksByCompletionThenPriority);

  if (dayTasks.length === 0) {
    return (
      <div className="flex h-full min-h-0 flex-col border rounded-lg p-6 bg-card">
        <h2 className="text-lg font-semibold mb-4 shrink-0">{displayTitle}</h2>
        <div className="flex flex-1 flex-col items-center justify-center text-center text-muted-foreground">
          <p>No tasks for this day</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col border rounded-lg p-4 bg-card">
      <h2 className="text-lg font-semibold mb-4 shrink-0">{displayTitle}</h2>
      <ScrollArea className="flex-1 min-h-0 pr-4">
        <div className="space-y-3">
          {dayTasks.map(task => {
            const claim = claimsMap.get(task.claimId);
            
            return (
              <TaskListItem
                key={task.id}
                task={task}
                claim={claim}
                onTaskComplete={onTaskComplete}
                onTaskEdit={onTaskEdit}
              />
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );
}
